import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RoleV2 } from '../../../types/role';
import {
  DataTable,
  type DataTableColumnMeta,
  type DataTableRenderers,
} from '../components/DataTable';
import { TableActionButton } from '../components/cells';

interface RolesTableProps {
  roles: RoleV2[];
  onEdit: (role: RoleV2) => void;
  onDelete: (role: RoleV2) => void;
}

/** Column shares below are shares of this; narrower viewports scroll the table instead. */
export const ROLES_TABLE_MIN_WIDTH = '800px';

export const ROLES_COLUMNS = [
  { key: 'name', labelKey: 'dashboard.tables.roles.name', width: '30%', cellClassName: 'cell-id', skeletonVariant: 'address' },
  { key: 'permissions', labelKey: 'dashboard.tables.roles.permissions', width: '16.5%', align: 'center', skeletonVariant: 'id' },
  { key: 'users', labelKey: 'dashboard.tables.roles.users', width: '20.5%', align: 'center', skeletonVariant: 'id' },
  { key: 'type', labelKey: 'dashboard.tables.roles.type', width: '19%', skeletonVariant: 'status' },
  { key: 'actions', labelKey: 'dashboard.tables.roles.actions', width: '14%', align: 'right', skeletonVariant: 'id' },
] as const satisfies readonly DataTableColumnMeta[];

export const RolesTable = memo(function RolesTable({
  roles,
  onEdit,
  onDelete,
}: RolesTableProps) {
  const { t } = useTranslation();

  const renderers = useMemo<DataTableRenderers<RoleV2, typeof ROLES_COLUMNS>>(
    () => ({
      name: (role) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {role.isBuiltIn && (
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 16, color: '#9ca3af' }}
              aria-hidden="true"
              title={t('dashboard.roles.builtIn')}
            >
              lock
            </span>
          )}
          <span style={{ fontWeight: 600 }}>{role.name}</span>
        </div>
      ),
      permissions: (role) => role.permissions.length,
      users: (role) => role.userCount,
      type: (role) =>
        role.isBuiltIn ? (
          <span className="role-badge role-badge-system">{t('dashboard.roles.builtIn')}</span>
        ) : (
          <span className="role-badge">{t('dashboard.roles.custom')}</span>
        ),
      actions: (role) => (
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <TableActionButton
            icon="edit"
            onClick={() => onEdit(role)}
            ariaLabel={t('dashboard.roles.editAria', { name: role.name })}
          />
          <TableActionButton
            icon="delete"
            onClick={() => onDelete(role)}
            disabled={role.isBuiltIn}
            title={
              role.isBuiltIn
                ? t('dashboard.roles.cannotDeleteBuiltIn')
                : t('dashboard.common.delete')
            }
            ariaLabel={t('dashboard.roles.deleteAria', { name: role.name })}
          />
        </div>
      ),
    }),
    [t, onEdit, onDelete],
  );

  return (
    <DataTable
      columns={ROLES_COLUMNS}
      renderers={renderers}
      rows={roles}
      rowKey={(role) => role.id}
      minWidth={ROLES_TABLE_MIN_WIDTH}
    />
  );
});

export default RolesTable;
